import { ASSESSMENT_FINAL_PAYLOAD_KEY, SECTION_ORDER, SKIN_ASM_CONSTANTS, SKIN_ASSESSMENT_QUESTION_TEMPLATE, SKIN_TYPE_MAP } from "../../../../common/skinAssessmentConstant";

export const getSectionId = (label = '') => label.split('.')?.[0] || label;

export const getQuestionIndex = (label = '') => {
  if (label.endsWith('.Q_PRODUCTS')) {
    return Number.MAX_SAFE_INTEGER;
  }

  const match = label.match(/Q(\d+)/);
  return match ? Number(match[1]) : -1;
}

export const isEntryQuestion = (label) => label === SKIN_ASM_CONSTANTS.M1 || label === SKIN_ASM_CONSTANTS.M1E;

export const getNextSectionId = (currentSectionId) => {
  const index = SECTION_ORDER.indexOf(currentSectionId);
  return SECTION_ORDER[index + 1] || null;
}

export const getFirstQuestionLabelOfSection = (section) => {
  if (!section || !section.questions?.length) return null;
  return section.questions[0].question_label;
}

export const getNextQuestionLabel = ({
  currentQuestionLabel,
  nextQuestionLabel,
  questionsBySection
}) => {
  // If next_question value given
  if (nextQuestionLabel) {
    return nextQuestionLabel;
  }

  if (isEntryQuestion(currentQuestionLabel)) {
    return null;
  }

  // Sequence inside same section
  const currSectionId = getSectionId(currentQuestionLabel);

  if (currSectionId === SKIN_ASM_CONSTANTS.M1 && currentQuestionLabel.includes('.Q')) {
    const match = currentQuestionLabel.match(/^M1\.([A-Z])\./);
    if (!match) return null;
    const branchKey = match[1];
    const branch = questionsBySection.M1?.branches?.[branchKey] || [];

    const index = branch.findIndex(
      q => q.question_label === currentQuestionLabel
    );

    if (branch[index + 1]) {
      return branch[index + 1]?.question_label;
    }

    const nextSectionId = getNextSectionId(SKIN_ASM_CONSTANTS.M1);
    const nextSection = questionsBySection[nextSectionId];

    return getFirstQuestionLabelOfSection(nextSection);
  }

  const currSection = questionsBySection[currSectionId];
  if (!currSection?.questions?.length) return null;

  const currentIndex = currSection.questions.findIndex(q => q.question_label === currentQuestionLabel);

  if (currSection.questions[currentIndex + 1]) {
    return currSection.questions[currentIndex + 1]?.question_label;
  }

  const nextSectionId = getNextSectionId(currSectionId);
  if (!nextSectionId) return null;

  const nextSection = questionsBySection[nextSectionId];

  return getFirstQuestionLabelOfSection(nextSection);
}

export const getSkinAssessmentQuestions = (assessmentQuestions) => {
  const sections = {};

  Object.keys(SKIN_ASSESSMENT_QUESTION_TEMPLATE).forEach((sectionId) => {
    sections[sectionId] = sectionId === SECTION_ORDER[0] ? {
      template: { ...SKIN_ASSESSMENT_QUESTION_TEMPLATE[sectionId] },
      entry: null,
      notSure: null,
      branches: {},
    } : {
      template: { ...SKIN_ASSESSMENT_QUESTION_TEMPLATE[sectionId] },
      questions: [],
    };
  });

  assessmentQuestions && assessmentQuestions.hw_assessments.forEach((question) => {
    const sectionId = getSectionId(question.question_label);
    if (!sections[sectionId]) return;

    if (sectionId === SECTION_ORDER[0]) {
      if (question.question_label === SECTION_ORDER[0]) {
        sections.M1.entry = question;
      } else if (question.question_label === SKIN_ASM_CONSTANTS.M1E) {
        sections.M1.notSure = question;
      } else {
        const match = question.question_label.match(/^M1\.([A-Z])\.Q\d+/);
        if (match) {
          const branchKey = match[1];
          if (!sections.M1.branches[branchKey]) {
            sections.M1.branches[branchKey] = [];
          }
          sections.M1.branches[branchKey].push(question);
        }
      }
    } else {
      sections[sectionId].questions.push(question);
    }
  });

  Object.keys(sections).forEach((sectionId) => {
    if (sectionId === SECTION_ORDER[0]) {
      Object.keys(sections.M1.branches).forEach(branchKey => {
        sections.M1.branches[branchKey].sort((a, b) => getQuestionIndex(a.question_label) - getQuestionIndex(b.question_label));
      });
      return;
    }
    sections[sectionId].questions.sort((a, b) => {
      return (
        getQuestionIndex(a.question_label) - getQuestionIndex(b.question_label)
      );
    });

    if (sectionId === SECTION_ORDER[0]) {
      sections.M1.template.question_count = Object.values(sections.M1.branches).reduce((sum, branch) => sum + branch.length, 0);
    } else {
      sections[sectionId].template.question_count = sections[sectionId].questions.filter(ques => !isEntryQuestion(ques.question_label)).length;
    }
  });

  return sections;
};

export const getNextLabelFromSavedAnswer = (question, savedAnswerIds) => {
  if (!question || !savedAnswerIds?.length) return null;

  const selectedOption = question?.question_value.find(opt => savedAnswerIds.includes(opt._id));

  return selectedOption?.next_question || null;
}

export const buildCurrentAnswer = (currentQuestion, savedAnswerIds) => {
  const selectedValues = currentQuestion.question_value.filter(
    (opt) => savedAnswerIds.includes(opt._id)
  );

  const healthInfo = selectedValues.find(v => v.healthData)?.healthData || 0;

  const answerIds = selectedValues
    .filter(v => !v.healthData)
    .map(v => v._id);

  const totalScore = selectedValues
    .filter(v => !v.healthData)
    .map(v => v.score);

  return {
    _id: currentQuestion._id,
    answer: answerIds,
    total_score: totalScore,
    ...(healthInfo && { healthInfo }),
  };
};

export const findQuestionLabelFromId = (questionId, questionsBySection) => {
  if (!questionId || !questionsBySection) return null;

  for (const sectionId of Object.keys(questionsBySection)) {
    const section = questionsBySection[sectionId];

    // M1 entry
    if (section.entry?._id === questionId) {
      return section.entry.question_label;
    }

    // M1 not sure
    if (section.notSure?._id === questionId) {
      return section.notSure.question_label;
    }

    // M1 branches
    if (section.branches) {
      for (const branchKey of Object.keys(section.branches)) {
        const found = section.branches[branchKey].find(
          q => q._id === questionId
        );
        if (found) return found.question_label;
      }
    }

    // Normal sections
    if (section.questions?.length) {
      const found = section.questions.find(q => q._id === questionId);
      if (found) return found.question_label;
    }
  }

  return null;
};

export const buildQuestionPath = ({
  startLabel,
  localAnswers,
  questionsBySection,
}) => {
  const path = [];
  let currentLabel = startLabel;
  while (true) {
    path.push(currentLabel);

    const saved = localAnswers[currentLabel];
    if (!saved) break;

    let nextLabel = null;

    // ENTRY QUESTIONS (M1 / M1.E)
    if (isEntryQuestion(currentLabel)) {
      const section = questionsBySection.M1;

      let question = null;
      if (currentLabel === SKIN_ASM_CONSTANTS.M1) question = section.entry;
      else if (currentLabel === SKIN_ASM_CONSTANTS.M1E) question = section.notSure;

      if (question) {
        const selectedOption = question.question_value.find(opt =>
          saved.saved_answer_ids.includes(opt._id)
        );
        nextLabel = selectedOption?.next_question || null;
      }
    }

    // NORMAL FLOW
    if (!nextLabel) {
      nextLabel = getNextQuestionLabel({
        currentQuestionLabel: currentLabel,
        nextQuestionLabel: null,
        questionsBySection,
      });
    }

    if (!nextLabel || path.includes(nextLabel)) break;

    currentLabel = nextLabel;
  }

  return path;
};

export const buildScoreMetaData = ({
  question,
  answerIds,
  skinType,
  previousAnswerIds = [],
}) => {
  if (!question || !answerIds?.length) return null;

  const sectionId = getSectionId(question.question_label);

  // Get key
  let key = '';
  if (sectionId === SKIN_ASM_CONSTANTS.M1) {
    key = skinType;
  } else {
    key = getScoreSkinTypeFromLabel(question.question_label);
  }

  const getScoreFromIds = (ids = []) =>
    question.question_value
      .filter(opt => ids.includes(opt._id))
      .reduce((sum, opt) => sum + Number(opt.score || 0), 0);

  const currentScore = getScoreFromIds(answerIds);
  const previousScore = getScoreFromIds(previousAnswerIds);

  const isUpdate = previousAnswerIds?.length > 0;

  return {
    key,
    score: isUpdate ? currentScore - previousScore : currentScore,
    maxScore: isUpdate
      ? 0
      : Math.max(...question.question_value.map(opt => Number(opt.score || 0))),
  };
};

export const getSkinType = ({ localAnswers, questionsBySection }) => {
  const m1 = localAnswers?.M1?.answer_ids?.[0];
  if (!m1) return null;

  const entry = questionsBySection?.M1?.entry;
  const entryOpt = entry?.question_value.find(o => o._id === m1);
  if (!entryOpt) return null;

  // direct answer
  if (entryOpt.key !== 'E') {
    return SKIN_TYPE_MAP[entryOpt.key] || null;
  }

  // NOT SURE
  const followUp = localAnswers?.[SKIN_ASM_CONSTANTS.M1E]?.answer_ids?.[0];
  if (!followUp) return 'notsure';

  const notSureQ = questionsBySection?.M1?.notSure;
  const followOpt = notSureQ?.question_value.find(o => o._id === followUp);

  return SKIN_TYPE_MAP[followOpt?.key] || 'notsure';
};

export const getScoreSkinTypeFromLabel = (questionLabel) => {
  const sectionId = getSectionId(questionLabel);
  return SKIN_ASSESSMENT_QUESTION_TEMPLATE[sectionId].key || null;
};

export const isAnonymousFlow = (label) => {
  return label === 'GENDER' || label === 'AGE';
};

export const isLastQuestion = ({
  currentQuestionLabel,
  nextQuestionLabel,
  questionsBySection
}) => {
  if (nextQuestionLabel) return false;

  if (isEntryQuestion(currentQuestionLabel)) return false;

  const next = getNextQuestionLabel({ currentQuestionLabel, nextQuestionLabel: null, questionsBySection });
  return !next;
}

export const saveFinalAssessmentPayload = (payload) => {
  if (!payload) return;
  sessionStorage.setItem(
    ASSESSMENT_FINAL_PAYLOAD_KEY,
    JSON.stringify(payload)
  );
};

export const getFinalAssessmentPayload = () => {
  const raw = sessionStorage.getItem(ASSESSMENT_FINAL_PAYLOAD_KEY);
  return raw ? JSON.parse(raw) : null;
};

export const clearFinalAssessmentPayload = () => {
  sessionStorage.removeItem(ASSESSMENT_FINAL_PAYLOAD_KEY);
};

